import { PageTransition } from '@/components/ui/page-transition';
import { cn } from '@/lib/cn';
import { useProfile } from '@/stores/profile';
import { useSession } from '@/stores/session';
import { emitSessionEnd, emitSessionStart } from '@/stores/signals';
import { useThemeEffect } from '@/stores/ui';
import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router';
import { MobileNav } from './mobile-nav';
import { MobileTopBar } from './mobile-top-bar';
import { Sidebar } from './sidebar';

// Module-level guard so React StrictMode's double-mount in dev doesn't fire
// the session:start signal twice for the same sessionId. Survives effect
// teardowns; the only way to "forget" a sessionId is to reset() the session,
// which mints a new one.
const startedSessions = new Set<string>();

export function AppShell() {
  useThemeEffect();
  const { pathname } = useLocation();
  const touch = useProfile((s) => s.touch);
  const sessionId = useSession((s) => s.sessionId);
  // Refresh lastSeenAt on every navigation inside the shell. Cheap and lets
  // future "Day n" math stay correct after the app goes through compaction.
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname is the intentional trigger.
  useEffect(() => {
    touch();
  }, [pathname, touch]);

  // Session lifecycle. Start on mount of a fresh sessionId; end on tab hide
  // or unload. `endSession` is idempotent — re-firing pagehide after a
  // visibilitychange→hidden is a no-op.
  useEffect(() => {
    if (!startedSessions.has(sessionId)) {
      startedSessions.add(sessionId);
      emitSessionStart();
    }
    let ended = false;
    const endSession = () => {
      if (ended) return;
      ended = true;
      const { startedAt, conceptIdsTouched } = useSession.getState();
      emitSessionEnd(Date.now() - startedAt, conceptIdsTouched);
    };
    const onVis = () => {
      if (document.visibilityState === 'hidden') endSession();
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', endSession);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pagehide', endSession);
    };
  }, [sessionId]);

  const isTutor = pathname === '/tutor' || pathname.startsWith('/tutor/');
  return (
    <div className="flex min-h-[100dvh] flex-col md:flex-row">
      <Sidebar />
      <div className="flex min-h-[100dvh] flex-1 flex-col md:h-[100dvh] md:min-h-0">
        <MobileTopBar />
        <main className={cn('relative flex-1 overflow-y-auto', isTutor ? 'pb-0' : 'pb-20 md:pb-0')}>
          <PageTransition>
            <Outlet />
          </PageTransition>
        </main>
        <MobileNav />
      </div>
    </div>
  );
}
